// @flow

import * as React from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-toastify';
import Config from '@insolus/config';
import { useTranslate } from '@insolus/react-i18n';
import authStore from 'stores/auth';
import useRequest from 'hooks/useRequest';
import catchApiError from 'utils/catchApiError';
import AdBookmarkSignin from '../../components/AdBookmarkSignin';
import ApiBookmarks from 'stores/api/bookmarks';
import {
    AdBookmarkActiveButton,
    AdBookmarkDefaultButton
} from '../../components/AdBookmarkButton';

import type { Bookmark } from 'stores/api/bookmarks';

const AdBookmarkElement = (): React.Node => {
    const advertiserId: number | null = authStore.user?.profile.id ?? null;
    const { Ad } = Config.get('data');

    const { __ } = useTranslate();

    const fetchRequest = useRequest((_advertiserId: number) => (
        ApiBookmarks.get(_advertiserId, Ad.id)
    ));
    const addRequest = useRequest((_advertiserId: number) => (
        ApiBookmarks.add(_advertiserId, Ad.id)
    ));
    const removeRequest = useRequest((id: number) => ApiBookmarks.remove(id));

    const [bookmark, setBookmark] = React.useState<Bookmark | null>(null);

    React.useEffect(() => {
        if (!advertiserId) { return; }

        const fetch = async () => {
            try {
                const result = await fetchRequest.execute(advertiserId);
                setBookmark(result);
            } catch (error) {
                toast.error(catchApiError(error));
            }
        };
        fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advertiserId]);

    const handleAdd = React.useCallback(async () => {
        if (!advertiserId) {
            toast.info(<AdBookmarkSignin />);
            return;
        }

        try {
            const result = await addRequest.execute(advertiserId);
            setBookmark(result);
            toast.success(__('ad-bookmark-added'));
        } catch (error) {
            toast.error(catchApiError(error));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advertiserId]);

    const handleRemove = React.useCallback(async () => {
        if (!bookmark) {
            return;
        }

        try {
            await removeRequest.execute(bookmark.id);
            setBookmark(null);
            toast.success(__('ad-bookmark-removed'));
        } catch (error) {
            toast.error(catchApiError(error));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookmark]);

    return bookmark
        ? <AdBookmarkActiveButton onClick={handleRemove} />
        : <AdBookmarkDefaultButton onClick={handleAdd} />;
};

export default (observer(AdBookmarkElement): typeof AdBookmarkElement);
